import { FileClock } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { useCan } from '@/lib/auth/session';

/**
 * O que a auditoria mostra ao abrir pelo atalho: o histórico de um KPI (tudo dele, ou só as metas ou os resultados) ou
 * de um registro; `name` é o nome mostrado na faixa "Histórico de …" (não depende da lista carregada).
 */
export type AuditScope = (
  { kpi: string; entity?: 'kpi_target' | 'kpi_result' } | { entity: string; record: string }
) & { name: string };

/** Caminho da auditoria filtrada (Configurações › Auditoria e histórico; T-202b, DEC-067). */
export function auditPath(organizationId: string, scope: AuditScope): string {
  const params = new URLSearchParams();
  if ('kpi' in scope) {
    params.set('kpi', scope.kpi);
    if (scope.entity) params.set('entidade', scope.entity);
  } else {
    params.set('entidade', scope.entity);
    params.set('registro', scope.record);
  }
  params.set('nome', scope.name);
  return `/empresas/${organizationId}/configuracoes/auditoria?${params.toString()}`;
}

/** Atalho "Auditoria" das telas de KPI, meta, objetivo e decisão: só para quem vê o módulo Auditoria. */
export function AuditLink({
  organizationId,
  scope,
  label,
  compact = false,
}: {
  organizationId: string;
  scope: AuditScope;
  /** Nome acessível: diz de quê é o histórico ("Auditoria de Receita"). */
  label: string;
  /** Só o ícone (linhas de lista); senão, ícone e o texto "Auditoria". */
  compact?: boolean;
}) {
  const can = useCan();
  if (!can('auditoria', 'view')) return null;
  const to = auditPath(organizationId, scope);
  return compact ? (
    <Button asChild variant="ghost" size="icon-sm">
      <Link to={to} aria-label={label} title="Auditoria">
        <FileClock aria-hidden />
      </Link>
    </Button>
  ) : (
    <Button asChild variant="outline" size="sm">
      <Link to={to} aria-label={label}>
        <FileClock aria-hidden />
        Auditoria
      </Link>
    </Button>
  );
}
